import { Component, Input, OnInit, OnChanges, SimpleChanges, EventEmitter, Output, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { MatOptionSelectionChange } from '@angular/material/core';
import { UserService } from '../../../services/user.service';
import { UserRequest, Country, State, UserDetailResponse, DepartmentData, UserHierarchicalData, TimeZone } from '../../../models/user.model';
import { MiscService } from 'src/app/services/misc.service';
import { CustomToastrService } from 'src/app/services/toastr.service';
import { LocationService } from 'src/app/modules/locations/services/location.service';
import { CustomerService } from 'src/app/services/customer.service';
import { ClientService } from 'src/app/modules/clients/services/client.service';
import { CommonService } from 'src/app/services/common.service';
import { UserRole, UserProfileService } from '../../../services/user-profile.service';
import { environment } from 'src/environments/environment';
import { combinePhone, splitPhone, DEFAULT_PHONE_COUNTRY_CODE } from 'src/app/shared/utilities/phone.helper';

@Component({
  selector: 'app-addedituser',
  templateUrl: './addedituser.component.html'
})
export class UserAddEditComponent implements OnInit, OnChanges {
  userForm: FormGroup;
  isEditMode = false;
  timeZones: TimeZone[] = [];
  countries: Country[] = [];
  countryCodes: string[] = [];
  states: State[] = [];
  roles: any[] = [];
  filteredRoles: any[] = [];
  clients: any[] = [];
  customers: any[] = [];
  currentUserRole: string = '';
  locations: any[] = [];
  buildings: any[] = [];
  floors: any[] = [];
  departments: any[] = [];
  isAllCustomersSelected = false;
  isAllLocationsSelected = false;
  isAllBuildingsSelected = false;
  isAllFloorsSelected = false;
  isAllDepartmentsSelected = false;
  temperatureUnits = [
    { value: 'Celsius', label: 'Celsius (°C)' },
    { value: 'Fahrenheit', label: 'Fahrenheit (°F)' }
  ];

  selectedIOTTimezoneId = '';
  selectedIOTRoleId = '';

  roleFieldsConfig: { [key: string]: string[] } = {
    [UserRole.CLIENT_ADMIN]: ['ClientId'],
    [UserRole.INSTALLER]: ['ClientId', 'CustomerIds'],
    [UserRole.USER]: ['ClientId', 'CustomerIds', 'LocationIds'],
    [UserRole.RETAIL_ADMIN]: ['ClientId', 'CustomerIds', 'LocationIds'],
    [UserRole.RETAIL_MANAGER]: ['ClientId', 'CustomerIds', 'LocationIds'],
    [UserRole.RETAIL_USER_WITH_CONTROL]: ['ClientId', 'CustomerIds', 'LocationIds'],
    [UserRole.RETAIL_USER]: ['ClientId', 'CustomerIds', 'LocationIds'],
    [UserRole.CUSTOMER_ADMIN]: ['ClientId', 'CustomerIds', 'LocationIds', 'BuildingIds', 'FloorIds', 'DepartmentIds'], //DeviceIds
    [UserRole.SUPER_ADMIN]: []
  };

  roleHierarchy: { [key: string]: string[] } = {
    [UserRole.SUPER_ADMIN]: [UserRole.CLIENT_ADMIN, UserRole.CUSTOMER_ADMIN, UserRole.INSTALLER, UserRole.RETAIL_ADMIN, UserRole.RETAIL_MANAGER, UserRole.RETAIL_USER_WITH_CONTROL, UserRole.RETAIL_USER, UserRole.SUPER_ADMIN, UserRole.USER],
    [UserRole.CLIENT_ADMIN]: [UserRole.CLIENT_ADMIN, UserRole.CUSTOMER_ADMIN, UserRole.INSTALLER, UserRole.RETAIL_ADMIN, UserRole.RETAIL_MANAGER, UserRole.RETAIL_USER_WITH_CONTROL, UserRole.RETAIL_USER, UserRole.USER],
    [UserRole.CUSTOMER_ADMIN]: [UserRole.CUSTOMER_ADMIN, UserRole.RETAIL_ADMIN, UserRole.RETAIL_MANAGER, UserRole.RETAIL_USER_WITH_CONTROL, UserRole.RETAIL_USER, UserRole.USER],
    [UserRole.RETAIL_ADMIN]: [UserRole.RETAIL_ADMIN, UserRole.RETAIL_MANAGER, UserRole.RETAIL_USER_WITH_CONTROL, UserRole.RETAIL_USER],
    [UserRole.RETAIL_MANAGER]: [UserRole.RETAIL_USER_WITH_CONTROL, UserRole.RETAIL_USER],
    [UserRole.RETAIL_USER_WITH_CONTROL]: [UserRole.RETAIL_USER],
    [UserRole.RETAIL_USER]: [],
    [UserRole.INSTALLER]: [],
    [UserRole.USER]: []

  };

  currentRoleFields: string[] = [];

  // Search Controls
  customerSearchControl = new FormControl('');
  locationSearchControl = new FormControl('');
  buildingSearchControl = new FormControl('');
  floorSearchControl = new FormControl('');
  departmentSearchControl = new FormControl('');

  // Form Controls for mat-select with ngx-mat-select-search
  customerIds = new FormControl<string[]>([]);
  locationIds = new FormControl<string[]>([]);
  buildingIds = new FormControl<string[]>([]);
  floorIds = new FormControl<string[]>([]);
  departmentIds = new FormControl<string[]>([]);

  // Filtered Arrays
  filteredCustomers: any[] = [];

  // Track if "Select All" was the last action
  selectAllClicked: boolean = false;
  filteredLocations: any[] = [];
  filteredBuildings: any[] = [];
  filteredFloors: any[] = [];
  filteredDepartments: any[] = [];

  @Input() userEditData: any;
  @Output() userSaved = new EventEmitter<any>();

  // For Mat-Dialog
  dialogData: any;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private clientService: ClientService,
    private customerService: CustomerService,
    private locationService: LocationService,
    private miscService: MiscService,
    private customToastrService: CustomToastrService,
    private commonService: CommonService,
    private userProfileService: UserProfileService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<UserAddEditComponent>
  ) {
    this.userForm = this.createForm();
    this.dialogData = data;
  }

  ngOnInit(): void {
    this.loadTimezone();
    this.loadCountries();
    this.loadRoles();
    this.initializeSearchFilters();
    this.setupCustomerSelectionSubscription();
    this.setupLocationSelectionSubscription();
    this.setupBuildingSelectionSubscription();
    this.setupFloorSelectionSubscription();
    this.setupDepartmentSelectionSubscription();

    // Check if we're in edit mode via dialog data
    if (this.dialogData && this.dialogData.userData) {
      this.isEditMode = true;
      this.userEditData = this.dialogData.userData;
      this.userForm.get('email')?.disable();
      // Form will be populated when roles are loaded in loadRoles()
    } else if (this.dialogData && this.dialogData.isEditMode) {
      this.isEditMode = this.dialogData.isEditMode;
      if (this.isEditMode && this.dialogData.userData) {
        this.userEditData = this.dialogData.userData;
        this.userForm.get('email')?.disable();
        // Form will be populated when roles are loaded in loadRoles()
      }
    } else {
      // Check if we're in edit mode via route parameter
      const id = this.route.snapshot.paramMap.get('id');
      if (id) {
        this.isEditMode = true;
        this.userForm.get('email')?.disable();
        this.loadUser();
      } else if (this.userEditData) {
        // Check if we're in edit mode via userEditData input
        this.isEditMode = true;
        this.userForm.get('email')?.disable();
        // Form will be populated when roles are loaded in loadRoles()
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userEditData'] && changes['userEditData'].currentValue) {
      this.isEditMode = true;
      this.userEditData = changes['userEditData'].currentValue;
      // Form will be populated when roles are loaded (if they're already loaded, populate now)
      if (this.roles && this.roles.length > 0) {
        this.populateForm(this.userEditData);
      }
    } else {
      this.isEditMode = false;
      this.userForm.reset();
    }
  }

  createForm(): FormGroup {
    return this.fb.group({
      userId: [null], // Use null for new users, will be set on edit

      // Account Details
      email: ['', [Validators.required, Validators.email, Validators.maxLength(250), UserAddEditComponent.publicDomainValidator]],
      systemRole: ['', [Validators.required]],
      displayName: ['', [Validators.maxLength(50)]],

      // Dynamic Role-Based Fields
      clientId: [''],
      customerIds: [[]],
      locationIds: [[]],
      buildingIds: [[]],
      floorIds: [[]],
      departmentIds: [[]],

      // Personal Details
      firstName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
      middleName: ['', [Validators.maxLength(50)]],
      lastName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
      phoneCountryCode: [DEFAULT_PHONE_COUNTRY_CODE],
      phone: ['', [Validators.required, Validators.maxLength(10), Validators.pattern('[0-9]*')]],
      temperatureUnit: ['Celsius', [Validators.required]],

      // Address Details
      timezoneId: ['', [Validators.required]],
      address1: ['', [Validators.required, Validators.maxLength(200)]],
      address2: ['', [Validators.maxLength(200)]],
      city: ['', [Validators.required, Validators.maxLength(50)]],
      countryGuid: [null, [Validators.required]],
      stateGuid: [null],
      zipcode: ['', [Validators.required, Validators.maxLength(10)]],

      isActive: [true]
    });
  }

  public static publicDomainValidator(control: FormControl) {
    if (control.value) {
      const domain = control.value.split('@')[1]?.toLowerCase();
      if (domain && environment.publicInboxDomains?.includes(domain)) {
        return { publicDomain: true };
      }
    }
    return null;
  }

  loadTimezone(): void {
    this.miscService.getTimeZones().subscribe({
      next: (timeZones) => {
        this.timeZones = timeZones;
        // If we're in edit mode and have user data, set the selected timezone ID
        if (this.isEditMode && this.userEditData && this.userEditData.timezoneId) {
          this.setSelectedTimezoneId(this.userEditData.timezoneId);
        }
      },
      error: (error) => {
        console.error('Error loading timezones:', error);
      }
    });
  }

  private setSelectedTimezoneId(timezoneId: string): void {
    const selectedTimezone = this.timeZones.find(timezone => timezone.id === timezoneId);
    if (selectedTimezone) {
      this.selectedIOTTimezoneId = selectedTimezone.iotTimezoneId || '';
    }
  }

  loadCountries(): void {
    this.miscService.getCountries().subscribe({
      next: (countries) => {
        this.countries = countries;
        this.countryCodes = this.buildCountryCodes(countries);
      },
      error: (error) => {
        console.error('Error loading countries:', error);
        this.customToastrService.error('Failed to load countries');
      }
    });
  }

  /**
   * Builds a de-duplicated, numerically sorted list of dialing codes for the
   * phone prefix dropdown.
   */
  private buildCountryCodes(countries: Country[]): string[] {
    const codes = new Set<string>();
    countries.forEach(c => {
      if (c.countryNumberCode) {
        codes.add(c.countryNumberCode);
      }
    });
    return Array.from(codes).sort((a, b) => Number(a) - Number(b));
  }

  private getTimezoneIdFallback(timezoneId: string): string {
    const selectedTimezone = this.timeZones.find(timezone => timezone.id === timezoneId);
    return selectedTimezone?.iotTimezoneId || '';
  }

  private getRoleIdFallback(roleId: string): string {
    const selectedRole = this.roles.find(role => role.id === roleId);
    return selectedRole?.iotRoleId || '';
  }

  loadRoles(): void {
    // Get current user's role from auth service or local storage
    this.getCurrentUserRole();

    this.userService.getRoles().subscribe({
      next: (roles) => {
        this.roles = roles;
        // Filter roles based on current user's role hierarchy
        this.filteredRoles = this.filterRolesByHierarchy(roles);
        // If we're in edit mode and have user data, populate form now that roles are loaded
        if (this.isEditMode && this.userEditData) {
          this.populateForm(this.userEditData);
        }
      },
      error: (error) => {
        console.error('Error loading roles:', error);
        this.customToastrService.error('Failed to load roles');
      }
    });
  }

  getCurrentUserRole(): void {
    // Try to get current user's role from localStorage data
    try {
      // Check for userProfile in localStorage (as seen in AuthService)
      const userProfile = localStorage.getItem('userProfile');
      if (userProfile) {
        const parsedProfile = JSON.parse(userProfile);
        if (parsedProfile && parsedProfile.role) {
          this.currentUserRole = parsedProfile.role;
          return;
        }
        // Also check for roleName or systemRole properties
        if (parsedProfile && parsedProfile.roleName) {
          this.currentUserRole = parsedProfile.roleName;
          return;
        }
        if (parsedProfile && parsedProfile.systemRole) {
          this.currentUserRole = parsedProfile.systemRole;
          return;
        }
      }

      // Fallback: try other common localStorage keys
      const userRole = localStorage.getItem('userRole') ||
        localStorage.getItem('currentUserRole') ||
        localStorage.getItem('role');

      if (userRole) {
        this.currentUserRole = userRole;
      } else {
        console.warn('Could not get current user role, defaulting to User');
        this.currentUserRole = UserRole.USER; // Default fallback using enum
      }
    } catch (error) {
      console.warn('Error parsing user profile, defaulting to User:', error);
      this.currentUserRole = UserRole.USER;
    }
  }

  /**
   * Helper function for case-insensitive role matching with enum values
   */
  private roleMatches(roleName: string, enumRole: UserRole): boolean {
    return roleName?.toLowerCase().trim() === enumRole.toLowerCase().trim();
  }

  /**
   * Get current user scope information for filtering data
   */
  private getCurrentUserScope(): { role: string, clientId?: string, customerIds?: string[], locationIds?: string[] } | null {
    const currentUserProfile = this.userProfileService.getCurrentUserProfile();
    if (!currentUserProfile) {
      return null;
    }

    // Extract scope information from user profile
    // This is a placeholder - you'll need to implement based on your user profile structure
    return {
      role: currentUserProfile.roleName?.trim() || '',
      // Add other scope properties based on your UserProfile model
      // clientId: currentUserProfile.clientId,
      // customerIds: currentUserProfile.customerIds,
      // locationIds: currentUserProfile.locationIds,
    };
  }

  filterRolesByHierarchy(allRoles: any[]): any[] {
    if (!this.currentUserRole || !this.roleHierarchy[this.currentUserRole]) {
      // If no current user role or hierarchy not defined, return all roles
      return allRoles;
    }

    const allowedRoleNames = this.roleHierarchy[this.currentUserRole];

    // Filter roles to only include those allowed by current user's role hierarchy
    return allRoles.filter(role => allowedRoleNames.includes(role.name));
  }

  // loadAllLocations(): void {
  //   // Load all locations directly for Retail Admin roles
  //   this.locationService.getAllLocations().subscribe({
  //     next: (locations) => {
  //       this.locations = locations;
  //       this.filteredLocations = [...locations];
  //       this.updateLocationSelectAllState();
  //     },
  //     error: (error) => {
  //       console.error('Error loading all locations:', error);
  //       this.customToastrService.error('Failed to load locations');
  //     }
  //   });
  // }

  loadClients(): Promise<void> {
    return new Promise((resolve) => {
      this.clientService.getClientslist().subscribe({
        next: (clients) => {
          this.clients = clients;
          // Re-apply selected clientId to ensure it stays selected after list refresh
          const currentClientId = this.userForm.get('clientId')?.value;
          if (currentClientId && currentClientId !== '') {
            this.userForm.patchValue({ clientId: currentClientId });
          }
          resolve();
        },
        error: (error) => {
          console.error('Error loading clients:', error);
          this.customToastrService.error('Failed to load clients');
          resolve();
        }
      });
    });
  }

  /**
   * Load role-based data cascading based on selected role and current user's scope
   */
  loadRoleBasedData(selectedRole: any): void {
    if (!selectedRole) {
      return;
    }

    // Skip data loading for Super Admin
    if (this.roleMatches(selectedRole.name, UserRole.SUPER_ADMIN)) {
      return;
    }

    const currentUserProfile = this.userProfileService.getCurrentUserProfile();

    if (!currentUserProfile) {
      return;
    }

    // Get current user's role for scope filtering
    const currentUserRole = currentUserProfile.roleName?.trim();

    // Load data cascading based on what the selected role needs
    this.loadDataCascadeForRole(currentUserRole, selectedRole.name);
  }

  /**
   * Load data cascade based on selected role requirements
   */
  private loadDataCascadeForRole(currentUserRole: string, selectedRoleName: string): void {
    const selectedRoleFields = this.roleFieldsConfig[selectedRoleName] || [];
    const needsClients = selectedRoleFields.includes('ClientId');
    const needsCustomers = selectedRoleFields.includes('CustomerIds') && !this.roleMatches(selectedRoleName, UserRole.SUPER_ADMIN);

    // For non-SuperAdmin roles that need client/customer data: fetch getMyCustomers once and share
    const isCurrentSuperAdmin = this.roleMatches(currentUserRole, UserRole.SUPER_ADMIN);

    if ((needsClients || needsCustomers) && !isCurrentSuperAdmin) {
      // Single getMyCustomers call to serve both client and customer loading
      this.customerService.getMyCustomers().subscribe({
        next: (myCustomers) => {
          if (needsClients) {
            const accessibleClientIds = [...new Set(myCustomers.map((c: any) => c.clientId))];
            this.clientService.getClientslist().subscribe({
              next: (allClients) => {
                this.clients = allClients.filter(client => accessibleClientIds.includes(client.id));
                const savedClientId = this.userForm.get('clientId')?.value;
                if (savedClientId) {
                  setTimeout(() => this.userForm.patchValue({ clientId: savedClientId }, { emitEvent: false }));
                }
              },
              error: (err) => console.error('Error loading clients:', err)
            });
          }

          if (needsCustomers) {
            this.customers = myCustomers.map((customer: any) => ({
              id: customer.id,
              name: customer.name,
              customerId: customer.customerId,
              clientId: customer.clientId
            }));
            this.filteredCustomers = [...this.customers];
            this.updateCustomerSelectAllState();
          }

          // Load locations if needed
          if (selectedRoleFields.includes('LocationIds')) {
            this.loadRoleBasedLocations(currentUserRole);
          }
        },
        error: (error) => {
          console.error('Error loading role-based customers:', error);
          this.customToastrService.error('Failed to load customers');
        }
      });
    } else {
      // SuperAdmin: use traditional approach
      if (needsClients) {
        this.loadRoleBasedClients(currentUserRole, selectedRoleName);
      }
      if (needsCustomers) {
        this.loadRoleBasedCustomers(currentUserRole);
      }
      if (selectedRoleFields.includes('LocationIds')) {
        this.loadRoleBasedLocations(currentUserRole);
      }
    }
  }

  /**
   * Load clients based on current user's role and scope
   */
  private loadRoleBasedClients(currentUserRole: string, selectedRoleName: string): void {
    // Super Admin can see all clients
    if (this.roleMatches(currentUserRole, UserRole.SUPER_ADMIN)) {
      this.loadClients();
    } else {
      // Other roles: load clients based on their scope using role-based APIs
      this.loadCurrentUserClients();
    }
  }

  /**
   * Load clients accessible to current user based on their role
   */
  private loadCurrentUserClients(): Promise<void> {
    return new Promise((resolve) => {
      const savedClientId = this.userForm.get('clientId')?.value;

      // Use the user management API to get users accessible to current user
      this.userService.getUsers().subscribe({
        next: (users) => {
          const accessibleClientIds = [...new Set(
            users
              .filter((user: any) => user.clientId || user.client?.id || user.companyId)
              .map((user: any) => user.clientId || user.client?.id || user.companyId)
          )];
          if (accessibleClientIds.length === 0) {
            this.loadCurrentUserClientsFromCustomers().then(() => resolve());
            return;
          }

          // Ensure the currently selected client is always included
          if (savedClientId && !accessibleClientIds.includes(savedClientId)) {
            accessibleClientIds.push(savedClientId);
          }

          this.clientService.getClientslist().subscribe({
            next: (allClients) => {
              this.clients = allClients.filter(client =>
                accessibleClientIds.includes(client.id) ||
                accessibleClientIds.includes(client.clientId) ||
                accessibleClientIds.includes(client.companyId)
              );
              // Re-apply selected clientId
              if (savedClientId) {
                setTimeout(() => {
                  this.userForm.patchValue({ clientId: savedClientId }, { emitEvent: false });
                  resolve();
                });
              } else {
                resolve();
              }
            },
            error: (error) => {
              console.error('Error loading all clients for filtering:', error);
              resolve();
            }
          });
        },
        error: (error) => {
          console.error('Error loading users for client filtering:', error);
          this.loadCurrentUserClientsFromCustomers().then(() => resolve());
        }
      });
    });
  }

  /**
   * Fallback method: Load clients based on accessible customers
   */
  private loadCurrentUserClientsFromCustomers(): Promise<void> {
    return new Promise((resolve) => {
      const savedClientId = this.userForm.get('clientId')?.value;

      this.customerService.getMyCustomers().subscribe({
        next: (customers) => {
          const accessibleClientIds = [...new Set(customers.map((customer: any) => customer.clientId))];

          // Ensure the currently selected client is always included
          if (savedClientId && !accessibleClientIds.includes(savedClientId)) {
            accessibleClientIds.push(savedClientId);
          }

          this.clientService.getClientslist().subscribe({
            next: (allClients) => {
              this.clients = allClients.filter(client => accessibleClientIds.includes(client.id));
              // Re-apply selected clientId
              if (savedClientId) {
                setTimeout(() => {
                  this.userForm.patchValue({ clientId: savedClientId }, { emitEvent: false });
                  resolve();
                });
              } else {
                resolve();
              }
            },
            error: (error) => {
              console.error('Error loading all clients for filtering:', error);
              this.loadClients().then(() => resolve());
            }
          });
        },
        error: (error) => {
          console.error('Error loading role-based customers for client filtering:', error);
          this.loadClients().then(() => resolve());
        }
      });
    });
  }

  /**
   * Filter clients based on current user's scope
   * This method should be implemented based on your user's assigned client hierarchy
   */
  private filterClientsForCurrentUser(allClients: any[]): any[] {
    const currentUserProfile = this.userProfileService.getCurrentUserProfile();
    if (!currentUserProfile) return allClients;

    const currentUserRole = currentUserProfile.roleName?.trim();

    // For Super Admin, return all clients
    if (this.roleMatches(currentUserRole, UserRole.SUPER_ADMIN)) {
      return allClients;
    }
    return allClients;
  }

  /**
   * Load customers based on current user's role and scope
   */
  private loadRoleBasedCustomers(currentUserRole: string): void {
    if (this.roleMatches(currentUserRole, UserRole.SUPER_ADMIN)) {
      // Super Admin: use traditional approach with clientId
      const clientId = this.userForm.get('clientId')?.value;
      if (clientId) {
        this.onClientChange(clientId);
      }
    } else {
      // Other roles: use role-based customer API
      this.customerService.getMyCustomers().subscribe({
        next: (customers) => {
          this.customers = customers.map((customer: any) => ({
            id: customer.id,
            name: customer.name,
            clientId: customer.clientId
          }));
          this.filteredCustomers = [...this.customers];
        },
        error: (error) => {
          console.error('Error loading role-based customers:', error);
          this.customToastrService.error('Failed to load customers');
        }
      });
    }
  }

  /**
   * Load locations based on current user's role and scope
   */
  private loadRoleBasedLocations(currentUserRole: string): void {
    if (this.roleMatches(currentUserRole, UserRole.SUPER_ADMIN) ||
      this.roleMatches(currentUserRole, UserRole.CLIENT_ADMIN) ||
      this.roleMatches(currentUserRole, UserRole.CUSTOMER_ADMIN)) {
      // These roles use traditional customer-based location loading
      const customerIds = this.customerIds.value || [];
      if (customerIds.length > 0) {
        this.loadLocationsByCustomers(customerIds);
      }
    } else {
      // Retail roles: use role-based location API
      this.locationService.getMyLocations().subscribe({
        next: (locations) => {
          this.locations = locations.map((location: any) => ({
            id: location.id,
            name: location.name,
            customerId: location.customerId,
            clientId: location.clientId
          }));
          this.filteredLocations = [...this.locations];
        },
        error: (error) => {
          console.error('Error loading role-based locations:', error);
          this.customToastrService.error('Failed to load locations');
        }
      });
    }
  }

  initializeSearchFilters(): void {
    // Initialize filtered arrays
    this.filteredCustomers = [...this.customers];
    this.filteredLocations = [...this.locations];
    this.filteredBuildings = [...this.buildings];
    this.filteredFloors = [...this.floors];
    this.filteredDepartments = [...this.departments];

    // Setup search filters
    this.customerSearchControl.valueChanges.subscribe(value => {
      this.filteredCustomers = this.filterOptions(this.customers, value || '');
      this.updateCustomerSelectAllState();
    });

    this.locationSearchControl.valueChanges.subscribe(value => {
      this.filteredLocations = this.filterOptions(this.locations, value || '');
      this.updateLocationSelectAllState();
    });

    this.buildingSearchControl.valueChanges.subscribe(value => {
      this.filteredBuildings = this.filterOptions(this.buildings, value || '');
      this.updateBuildingSelectAllState();
    });

    this.floorSearchControl.valueChanges.subscribe(value => {
      this.filteredFloors = this.filterOptions(this.floors, value || '');
      this.updateFloorSelectAllState();
    });

    this.departmentSearchControl.valueChanges.subscribe(value => {
      this.filteredDepartments = this.filterOptions(this.departments, value || '');
      this.updateDepartmentSelectAllState();
    });
  }

  setupCustomerSelectionSubscription(): void {
    // Subscribe to customerIds changes to sync with userForm
    this.customerIds.valueChanges.subscribe(value => {
      const cleanValue = (value || []).filter((val: string) => val !== 'selectAll');
      this.userForm.patchValue({ customerIds: cleanValue }, { emitEvent: false });
      this.updateCustomerSelectAllState();
      this.loadLocationsByCustomers(cleanValue);
    });

    // Also sync the other way - from userForm to customerIds
    this.userForm.get('customerIds')?.valueChanges.subscribe(value => {
      if (value && JSON.stringify(this.customerIds.value) !== JSON.stringify(value)) {
        this.customerIds.setValue(value, { emitEvent: false });
      }
    });
  }

  setupLocationSelectionSubscription(): void {
    this.locationIds.valueChanges.subscribe(value => {
      const cleanValue = (value || []).filter((val: string) => val !== 'selectAll');
      this.userForm.patchValue({ locationIds: cleanValue }, { emitEvent: false });
      this.updateLocationSelectAllState();
      this.loadBuildingsByLocations(cleanValue);
    });
  }

  setupBuildingSelectionSubscription(): void {
    this.buildingIds.valueChanges.subscribe(value => {
      const cleanValue = (value || []).filter((val: string) => val !== 'selectAll');
      this.userForm.patchValue({ buildingIds: cleanValue }, { emitEvent: false });
      this.updateBuildingSelectAllState();
      this.loadFloorsByBuildings(cleanValue);
    });
  }

  setupFloorSelectionSubscription(): void {
    this.floorIds.valueChanges.subscribe(value => {
      const cleanValue = (value || []).filter((val: string) => val !== 'selectAll');
      this.userForm.patchValue({ floorIds: cleanValue }, { emitEvent: false });
      this.updateFloorSelectAllState();
      this.loadDepartmentsByFloors(cleanValue);
    });
  }

  setupDepartmentSelectionSubscription(): void {
    this.departmentIds.valueChanges.subscribe(value => {
      const cleanValue = (value || []).filter((val: string) => val !== 'selectAll');
      this.userForm.patchValue({ departmentIds: cleanValue }, { emitEvent: false });
      this.updateDepartmentSelectAllState();
    });
  }

  private filterOptions(options: any[], searchValue: string): any[] {
    if (!searchValue) {
      return options;
    }
    return options.filter(option =>
      option.name.toLowerCase().includes(searchValue.toLowerCase())
    );
  }

  onCountryChange(countryId: string, preserveStateId?: string): void {
    this.states = [];
    if (!preserveStateId) {
      this.userForm.get('stateGuid')?.setValue(null);
    }
    if (countryId) {
      this.miscService.getStatesByCountryId(countryId).subscribe({
        next: (states) => {
          this.states = states;
          // If we have a stateId to preserve (from edit mode), set it after states are loaded
          if (preserveStateId) {
            const stateExists = states.find(state => state.id === preserveStateId);
            if (stateExists) {
              this.userForm.get('stateGuid')?.setValue(preserveStateId);
            }
          }
        },
        error: (error) => {
          console.error('Error loading states:', error);
          this.customToastrService.error('Failed to load states');
        }
      });
    }
  }

  onTimezoneChange(): void {
    const timezoneId = this.userForm.get('timezoneId')?.value;
    // Store IOTTimezoneId from selected timezone
    const selectedTimezone = this.timeZones.find(timezone => timezone.id === timezoneId);
    if (selectedTimezone) {
      this.selectedIOTTimezoneId = selectedTimezone.iotTimezoneId || '';
    } else {
      this.selectedIOTTimezoneId = '';
    }
  }

  onRoleChange(): void {
    const roleId = this.userForm.get('systemRole')?.value;
    if (roleId) {
      // Extract IOTRoleId from selected role
      const selectedRole = this.roles.find(role => role.id === roleId);
      if (selectedRole) {
        this.selectedIOTRoleId = selectedRole.iotRoleId || '';
      } else {
        this.selectedIOTRoleId = '';
      }

      this.updateRoleBasedFields(roleId);
      this.resetDynamicFields();
      this.updateFieldValidators();

      // Load role-based data cascading
      this.loadRoleBasedData(selectedRole);
    }
  }

  resetDynamicFields(): void {
    this.userForm.patchValue({
      clientId: '',
      customerIds: [],
      locationIds: [],
      buildingIds: [],
      floorIds: [],
      departmentIds: []
    });
    this.customerIds.setValue([]);
    this.locationIds.setValue([]);
    this.buildingIds.setValue([]);
    this.floorIds.setValue([]);
    this.departmentIds.setValue([]);
    this.customers = [];
    this.locations = [];
    this.buildings = [];
    this.floors = [];
    this.departments = [];
  }

  updateFieldValidators(): void {
    const fieldsToValidate = ['clientId', 'customerIds', 'locationIds', 'buildingIds', 'floorIds', 'departmentIds'];

    fieldsToValidate.forEach(field => {
      const control = this.userForm.get(field);
      if (control) {
        if (this.currentRoleFields.includes(field.charAt(0).toUpperCase() + field.slice(1))) {
          control.setValidators([Validators.required]);
        } else {
          control.clearValidators();
        }
        control.updateValueAndValidity();
      }
    });
  }

  onClientChange(clientId: string): void {
    // While creating a new user, auto-populate the address details from the
    // selected client. Skipped in edit mode so an existing user's saved
    // address is not overwritten when the form is populated programmatically.
    if (clientId && !this.isEditMode) {
      this.autofillAddressFromClient(clientId);
    }

    this.customers = [];
    this.locations = [];
    this.buildings = [];
    this.floors = [];
    this.departments = [];

    // Clear filtered arrays
    this.filteredCustomers = [];
    this.filteredLocations = [];
    this.filteredBuildings = [];
    this.filteredFloors = [];
    this.filteredDepartments = [];

    this.userForm.patchValue({
      customerIds: [],
      locationIds: [],
      buildingIds: [],
      floorIds: [],
      departmentIds: []
    });
    this.customerIds.setValue([]);
    this.locationIds.setValue([]);
    this.buildingIds.setValue([]);
    this.floorIds.setValue([]);
    this.departmentIds.setValue([]);

    if (clientId && this.currentRoleFields.includes('CustomerIds')) {
      // Use role-based customer loading
      const currentUserProfile = this.userProfileService.getCurrentUserProfile();
      const currentUserRole = currentUserProfile?.roleName?.trim();

      if (currentUserRole && this.roleMatches(currentUserRole, UserRole.SUPER_ADMIN)) {
        // Super Admin: traditional client-based loading
        this.customerService.getCustomersListByClientId(clientId).subscribe({
          next: (customers) => {
            this.customers = customers;
            this.filteredCustomers = [...customers];
            this.updateCustomerSelectAllState();
          },
          error: (error) => {
            console.error('Error loading customers:', error);
            this.customToastrService.error('Failed to load customers');
          }
        });
      } else {
        // Other roles: use role-based API
        this.customerService.getMyCustomers().subscribe({
          next: (customers) => {
            // Filter customers to only those belonging to the selected client
            this.customers = customers.filter((customer: any) => customer.clientId === clientId);
            this.filteredCustomers = [...this.customers];
            this.updateCustomerSelectAllState();
          },
          error: (error) => {
            console.error('Error loading role-based customers:', error);
            this.customToastrService.error('Failed to load customers');
          }
        });
      }
    }
  }

  /**
   * Fetch the selected client's details and auto-populate the user's
   * Address Details section (address1, address2, city, zipcode, country, state).
   */
  private autofillAddressFromClient(clientId: string): void {
    this.clientService.getClientById(clientId).subscribe({
      next: (client) => {
        if (!client) {
          return;
        }

        this.userForm.patchValue({
          address1: client.address1 ?? '',
          address2: client.address2 ?? '',
          city: client.city ?? '',
          zipcode: client.zipcode ?? ''
        });

        // Set country, then load its states and select the client's state.
        if (client.countryId) {
          this.userForm.patchValue({ countryGuid: client.countryId });
          // onCountryChange loads states for the country and, when a stateId is
          // passed, selects it once the states have loaded.
          this.onCountryChange(client.countryId, client.stateId ?? undefined);
        } else {
          this.userForm.patchValue({ countryGuid: null, stateGuid: null });
          this.states = [];
        }
      },
      error: (error) => {
        console.error('Error loading client details for address auto-fill:', error);
        this.customToastrService.error('Failed to load client address details');
      }
    });
  }

  onCustomerToggle(customerId: string, isChecked: boolean): void {
    const currentValues = this.customerIds.value || [];
    let updatedValues: string[];

    if (isChecked) {
      updatedValues = [...currentValues, customerId];
    } else {
      updatedValues = currentValues.filter((id: string) => id !== customerId);
    }

    this.customerIds.setValue(updatedValues);
    // Reset select all flag when individual selections are made
    this.selectAllClicked = false;
    this.loadLocationsByCustomers(updatedValues);
  }

  private loadLocationsByCustomers(customerIds: string[]): void {
    if (!customerIds || customerIds.length === 0) {
      this.locations = [];
      this.filteredLocations = [];
      this.locationIds.setValue([], { emitEvent: false });
      this.userForm.patchValue({ locationIds: [] }, { emitEvent: false });
      this.loadBuildingsByLocations([]);
      return;
    }

    if (this.currentRoleFields.includes('LocationIds')) {
      this.locationService.getLocationsByCustomerIds(customerIds).subscribe({
        next: (locations) => {
          this.locations = locations;
          this.filteredLocations = [...locations];

          const currentSelected = this.locationIds.value || [];
          const validSelected = currentSelected.filter(id => locations.some(loc => loc.id === id));

          this.locationIds.setValue(validSelected, { emitEvent: false });
          this.userForm.patchValue({ locationIds: validSelected }, { emitEvent: false });

          this.updateLocationSelectAllState();
          this.loadBuildingsByLocations(validSelected);
        },
        error: (error) => {
          console.error('Error loading locations:', error);
          this.customToastrService.error('Failed to load locations');
        }
      });
    }
  }

  isCustomerSelected(customerId: string): boolean {
    const currentValues = this.customerIds.value || [];
    return currentValues.includes(customerId);
  }

  resetFilter(): void {
    this.customerSearchControl.setValue('');
    this.filteredCustomers = [...this.customers];
    this.selectAllClicked = false;
    this.updateCustomerSelectAllState();
  }

  toggleCustomerSelectAll(event: MatOptionSelectionChange): void {
    if (!event.isUserInput) return;

    const filteredCustomerIds = this.filteredCustomers.map(c => c.id);

    if (!this.isAllCustomersSelected) {
      const newSelection = [...filteredCustomerIds, 'selectAll'];
      this.customerIds.setValue(newSelection);
      this.isAllCustomersSelected = true;
    } else {
      this.customerIds.setValue([]);
      this.isAllCustomersSelected = false;
    }
  }

  updateCustomerSelectAllState(): void {
    // Get filtered customer IDs
    const allFilteredCustomerIds = this.customers.map(c => c.id);

    // Get selected customer IDs (excluding 'selectAll')
    const selectedCustomerIds = (this.customerIds.value || []).filter((id: string) => id !== 'selectAll');

    // Check if all filtered customers are selected
    const allSelected = allFilteredCustomerIds.length > 0 &&
      allFilteredCustomerIds.every(id => selectedCustomerIds.includes(id));

    // Update state only if it has changed
    if (allSelected !== this.isAllCustomersSelected) {
      this.isAllCustomersSelected = allSelected;
    }

    // Ensure consistency between customerIds FormControl and selection state
    if (this.isAllCustomersSelected && !(this.customerIds.value || []).includes('selectAll')) {
      const newSelection = [...allFilteredCustomerIds, 'selectAll'];
      this.customerIds.setValue(newSelection, { emitEvent: false });
    } else if (!this.isAllCustomersSelected && (this.customerIds.value || []).includes('selectAll')) {
      this.customerIds.setValue(selectedCustomerIds, { emitEvent: false });
    }
  }

  // Location Select All Methods
  toggleLocationSelectAll(event: MatOptionSelectionChange): void {
    if (!event.isUserInput) return;

    const filteredLocationIds = this.filteredLocations.map(l => l.id);

    if (!this.isAllLocationsSelected) {
      const newSelection = [...filteredLocationIds, 'selectAll'];
      this.locationIds.setValue(newSelection);
      this.isAllLocationsSelected = true;
    } else {
      this.locationIds.setValue([]);
      this.isAllLocationsSelected = false;
    }
  }

  updateLocationSelectAllState(): void {
    const allAvailableLocationIds = this.locations.map(l => l.id);
    const selectedLocationIds = (this.locationIds.value || []).filter((id: string) => id !== 'selectAll');

    const allSelected = allAvailableLocationIds.length > 0 &&
      allAvailableLocationIds.every(id => selectedLocationIds.includes(id));

    if (allSelected !== this.isAllLocationsSelected) {
      this.isAllLocationsSelected = allSelected;
    }

    if (this.isAllLocationsSelected && !(this.locationIds.value || []).includes('selectAll')) {
      const newSelection = [...allAvailableLocationIds, 'selectAll'];
      this.locationIds.setValue(newSelection, { emitEvent: false });
    } else if (!this.isAllLocationsSelected && (this.locationIds.value || []).includes('selectAll')) {
      this.locationIds.setValue(selectedLocationIds, { emitEvent: false });
    }
  }

  // Building Select All Methods
  toggleBuildingSelectAll(event: MatOptionSelectionChange): void {
    if (!event.isUserInput) return;

    const filteredBuildingIds = this.filteredBuildings.map(b => b.id);

    if (!this.isAllBuildingsSelected) {
      const newSelection = [...filteredBuildingIds, 'selectAll'];
      this.buildingIds.setValue(newSelection);
      this.isAllBuildingsSelected = true;
    } else {
      this.buildingIds.setValue([]);
      this.isAllBuildingsSelected = false;
    }
  }

  updateBuildingSelectAllState(): void {
    const allAvailableBuildingIds = this.buildings.map(b => b.id);
    const selectedBuildingIds = (this.buildingIds.value || []).filter((id: string) => id !== 'selectAll');

    const allSelected = allAvailableBuildingIds.length > 0 &&
      allAvailableBuildingIds.every(id => selectedBuildingIds.includes(id));

    if (allSelected !== this.isAllBuildingsSelected) {
      this.isAllBuildingsSelected = allSelected;
    }

    if (this.isAllBuildingsSelected && !(this.buildingIds.value || []).includes('selectAll')) {
      const newSelection = [...allAvailableBuildingIds, 'selectAll'];
      this.buildingIds.setValue(newSelection, { emitEvent: false });
    } else if (!this.isAllBuildingsSelected && (this.buildingIds.value || []).includes('selectAll')) {
      this.buildingIds.setValue(selectedBuildingIds, { emitEvent: false });
    }
  }

  // Floor Select All Methods
  toggleFloorSelectAll(event: MatOptionSelectionChange): void {
    if (!event.isUserInput) return;

    const filteredFloorIds = this.filteredFloors.map(f => f.id);

    if (!this.isAllFloorsSelected) {
      const newSelection = [...filteredFloorIds, 'selectAll'];
      this.floorIds.setValue(newSelection);
      this.isAllFloorsSelected = true;
    } else {
      this.floorIds.setValue([]);
      this.isAllFloorsSelected = false;
    }
  }

  updateFloorSelectAllState(): void {
    const allAvailableFloorIds = this.floors.map(f => f.id);
    const selectedFloorIds = (this.floorIds.value || []).filter((id: string) => id !== 'selectAll');

    const allSelected = allAvailableFloorIds.length > 0 &&
      allAvailableFloorIds.every(id => selectedFloorIds.includes(id));

    if (allSelected !== this.isAllFloorsSelected) {
      this.isAllFloorsSelected = allSelected;
    }

    if (this.isAllFloorsSelected && !(this.floorIds.value || []).includes('selectAll')) {
      const newSelection = [...allAvailableFloorIds, 'selectAll'];
      this.floorIds.setValue(newSelection, { emitEvent: false });
    } else if (!this.isAllFloorsSelected && (this.floorIds.value || []).includes('selectAll')) {
      this.floorIds.setValue(selectedFloorIds, { emitEvent: false });
    }
  }

  // Department Select All Methods
  toggleDepartmentSelectAll(event: MatOptionSelectionChange): void {
    if (!event.isUserInput) return;

    const filteredDepartmentIds = this.filteredDepartments.map(d => d.id);

    if (!this.isAllDepartmentsSelected) {
      const newSelection = [...filteredDepartmentIds, 'selectAll'];
      this.departmentIds.setValue(newSelection);
      this.isAllDepartmentsSelected = true;
    } else {
      this.departmentIds.setValue([]);
      this.isAllDepartmentsSelected = false;
    }
  }

  updateDepartmentSelectAllState(): void {
    const allAvailableDepartmentIds = this.departments.map(d => d.id);
    const selectedDepartmentIds = (this.departmentIds.value || []).filter((id: string) => id !== 'selectAll');

    const allSelected = allAvailableDepartmentIds.length > 0 &&
      allAvailableDepartmentIds.every(id => selectedDepartmentIds.includes(id));

    if (allSelected !== this.isAllDepartmentsSelected) {
      this.isAllDepartmentsSelected = allSelected;
    }

    if (this.isAllDepartmentsSelected && !(this.departmentIds.value || []).includes('selectAll')) {
      const newSelection = [...allAvailableDepartmentIds, 'selectAll'];
      this.departmentIds.setValue(newSelection, { emitEvent: false });
    } else if (!this.isAllDepartmentsSelected && (this.departmentIds.value || []).includes('selectAll')) {
      this.departmentIds.setValue(selectedDepartmentIds, { emitEvent: false });
    }
  }

  onLocationToggle(location: any, isChecked: boolean): void {
    const currentValues = this.userForm.get('locationIds')?.value || [];
    let updatedValues: string[];

    if (isChecked) {
      updatedValues = [...currentValues, location.id];
    } else {
      updatedValues = currentValues.filter((id: string) => id !== location.id);
    }

    this.userForm.patchValue({ locationIds: updatedValues });
    this.loadBuildingsByLocations(updatedValues);
  }

  private loadBuildingsByLocations(locationIds: string[]): void {
    if (!locationIds || locationIds.length === 0) {
      this.buildings = [];
      this.filteredBuildings = [];
      this.buildingIds.setValue([], { emitEvent: false });
      this.userForm.patchValue({ buildingIds: [] }, { emitEvent: false });
      this.loadFloorsByBuildings([]);
      return;
    }

    if (this.currentRoleFields.includes('BuildingIds')) {
      this.locationService.getBuildingsByLocationIds(locationIds).subscribe({
        next: (buildings) => {
          this.buildings = buildings;
          this.filteredBuildings = [...buildings];

          const currentSelected = this.buildingIds.value || [];
          const validSelected = currentSelected.filter(id => buildings.some(b => b.id === id));

          this.buildingIds.setValue(validSelected, { emitEvent: false });
          this.userForm.patchValue({ buildingIds: validSelected }, { emitEvent: false });

          this.updateBuildingSelectAllState();
          this.loadFloorsByBuildings(validSelected);
        },
        error: (error) => {
          console.error('Error loading buildings:', error);
          this.customToastrService.error('Failed to load buildings');
        }
      });
    }
  }

  isLocationSelected(locationId: string): boolean {
    const currentValues = this.userForm.get('locationIds')?.value || [];
    return currentValues.includes(locationId);
  }

  onBuildingToggle(building: any, isChecked: boolean): void {
    const currentValues = this.userForm.get('buildingIds')?.value || [];
    let updatedValues: string[];

    if (isChecked) {
      updatedValues = [...currentValues, building.id];
    } else {
      updatedValues = currentValues.filter((id: string) => id !== building.id);
    }

    this.userForm.patchValue({ buildingIds: updatedValues });
    this.loadFloorsByBuildings(updatedValues);
  }

  private loadFloorsByBuildings(buildingIds: string[]): void {
    if (!buildingIds || buildingIds.length === 0) {
      this.floors = [];
      this.filteredFloors = [];
      this.floorIds.setValue([], { emitEvent: false });
      this.userForm.patchValue({ floorIds: [] }, { emitEvent: false });
      this.loadDepartmentsByFloors([]);
      return;
    }

    if (this.currentRoleFields.includes('FloorIds')) {
      this.locationService.getFloorsByBuildingIds(buildingIds).subscribe({
        next: (floors) => {
          this.floors = floors;
          this.filteredFloors = [...floors];

          const currentSelected = this.floorIds.value || [];
          const validSelected = currentSelected.filter(id => floors.some(f => f.id === id));

          this.floorIds.setValue(validSelected, { emitEvent: false });
          this.userForm.patchValue({ floorIds: validSelected }, { emitEvent: false });

          this.updateFloorSelectAllState();
          this.loadDepartmentsByFloors(validSelected);
        },
        error: (error) => {
          console.error('Error loading floors:', error);
          this.customToastrService.error('Failed to load floors');
        }
      });
    }
  }

  isBuildingSelected(buildingId: string): boolean {
    const currentValues = this.userForm.get('buildingIds')?.value || [];
    return currentValues.includes(buildingId);
  }

  onFloorToggle(floor: any, isChecked: boolean): void {
    const currentValues = this.userForm.get('floorIds')?.value || [];
    let updatedValues: string[];

    if (isChecked) {
      updatedValues = [...currentValues, floor.id];
    } else {
      updatedValues = currentValues.filter((id: string) => id !== floor.id);
    }

    this.userForm.patchValue({ floorIds: updatedValues });
    this.loadDepartmentsByFloors(updatedValues);
  }

  private loadDepartmentsByFloors(floorIds: string[]): void {
    if (!floorIds || floorIds.length === 0) {
      this.departments = [];
      this.filteredDepartments = [];
      this.departmentIds.setValue([], { emitEvent: false });
      this.userForm.patchValue({ departmentIds: [] }, { emitEvent: false });
      return;
    }

    if (this.currentRoleFields.includes('DepartmentIds')) {
      this.locationService.getDepartmentsByFloorIds(floorIds).subscribe({
        next: (departments) => {
          this.departments = departments;
          this.filteredDepartments = [...departments];

          const currentSelected = this.departmentIds.value || [];
          const validSelected = currentSelected.filter(id => departments.some(d => d.id === id));

          this.departmentIds.setValue(validSelected, { emitEvent: false });
          this.userForm.patchValue({ departmentIds: validSelected }, { emitEvent: false });

          this.updateDepartmentSelectAllState();
        },
        error: (error) => {
          console.error('Error loading departments:', error);
          this.customToastrService.error('Failed to load departments');
        }
      });
    }
  }

  isFloorSelected(floorId: string): boolean {
    const currentValues = this.userForm.get('floorIds')?.value || [];
    return currentValues.includes(floorId);
  }

  onDepartmentToggle(department: any, isChecked: boolean): void {
    const currentValues = this.userForm.get('departmentIds')?.value || [];
    let updatedValues: string[];

    if (isChecked) {
      updatedValues = [...currentValues, department.id];
    } else {
      updatedValues = currentValues.filter((id: string) => id !== department.id);
    }

    this.userForm.patchValue({ departmentIds: updatedValues });
  }

  isDepartmentSelected(departmentId: string): boolean {
    const currentValues = this.userForm.get('departmentIds')?.value || [];
    return currentValues.includes(departmentId);
  }

  shouldShowField(fieldName: string): boolean {
    return this.currentRoleFields.includes(fieldName);
  }

  getDepartmentIdsBasedOnRole(roleId: string): string[] {
    const selectedRole = this.roles.find(r => r.id === roleId);
    if (!selectedRole) return [];

    const roleName = selectedRole.name;

    // Map role-specific entity IDs to departmentIds based on role requirements
    // The departmentIds field in backend stores different entity types based on role
    if (this.roleMatches(roleName, UserRole.INSTALLER)) {
      // For Installer: customerIds are stored in departmentIds field
      return (this.customerIds.value || []).filter((id: string) => id !== 'selectAll');
    } else if (
      this.roleMatches(roleName, UserRole.USER) ||
      this.roleMatches(roleName, UserRole.RETAIL_ADMIN) ||
      this.roleMatches(roleName, UserRole.RETAIL_MANAGER) ||
      this.roleMatches(roleName, UserRole.RETAIL_USER) ||
      this.roleMatches(roleName, UserRole.RETAIL_USER_WITH_CONTROL)
    ) {
      // For User roles: locationIds are stored in departmentIds field
      return (this.locationIds.value || []).filter((id: string) => id !== 'selectAll');
    } else if (this.roleMatches(roleName, UserRole.CUSTOMER_ADMIN)) {
      // For Customer Admin: actual departmentIds are stored in departmentIds field
      return (this.departmentIds.value || []).filter((id: string) => id !== 'selectAll');
    } else if (this.roleMatches(roleName, UserRole.CLIENT_ADMIN)) {
      return [this.userForm.get('clientId')?.value || null];
    } else if (this.roleMatches(roleName, UserRole.SUPER_ADMIN)) {
      // For Super Admin: no specific entity mapping
      return [];
    } else {
      // Default case for unknown roles
      return [];
    }
  }

  loadUser(): void {
    if (this.userEditData) {
      // Use provided user data
      this.populateForm(this.userEditData);
    } else {
      // Load user data from API using route parameter
      const id = this.route.snapshot.paramMap.get('id');
      if (id) {
        this.userService.getUserById(id).subscribe({
          next: (user) => {
            this.populateForm(user);
          },
          error: (error) => {
            console.error('Error loading user:', error);
            this.customToastrService.error('Failed to load user data');
          }
        });
      }
    }
  }

  populateForm(user: UserDetailResponse): void {
    // 1. FIRST - Set the roleId and update field visibility
    this.userForm.patchValue({
      systemRole: user.roleId  // Set role first (form control is named systemRole)
    });

    // 2. Update role-based field visibility immediately after setting roleId
    this.updateRoleBasedFields(user.roleId);

    // 2b. Set the IOT Role ID and load clients list, then populate role-specific fields
    const selectedRole = this.roles.find(role => role.id === user.roleId);
    let clientLoadPromise: Promise<void> = Promise.resolve();
    if (selectedRole) {
      this.selectedIOTRoleId = selectedRole.iotRoleId || '';
      // Load the client list for the dropdown - populateRoleSpecificFields will run after it completes
      const currentUserProfile = this.userProfileService.getCurrentUserProfile();
      const currentUserRole = currentUserProfile?.roleName?.trim();
      if (currentUserRole && this.roleMatches(currentUserRole, UserRole.SUPER_ADMIN)) {
        clientLoadPromise = this.loadClients();
      } else {
        clientLoadPromise = this.loadCurrentUserClients();
      }
    }

    // 3. THEN - Populate other form fields
    const { countryCode: phoneCountryCode, number: phoneNumber } = splitPhone(user.phone);
    this.userForm.patchValue({
      userId: user.id,
      email: user.email,
      displayName: user.displayName,
      firstName: user.firstName,
      middleName: user.middleName,
      lastName: user.lastName,
      phoneCountryCode: phoneCountryCode,
      phone: phoneNumber,
      temperatureUnit: user.temperatureUnit || 'Celsius',
      timezoneId: user.timezoneId || '',
      address1: user.address1,
      address2: user.address2,
      city: user.city,
      countryGuid: this.commonService.parseNullValue(user.countryId),
      stateGuid: this.commonService.parseNullValue(user.stateId),
      zipcode: user.zipcode,
      isActive: user.isActive
    });

    // 4. Handle role-specific dynamic fields AFTER clients are loaded
    clientLoadPromise.then(() => {
      if (user.hierarchicalData) {
        this.populateRoleSpecificFields(user.hierarchicalData, user.roleId);
      }
    });

    // 5. Load states if country is selected
    if (user.countryId) {
      this.onCountryChange(user.countryId, user.stateId);
    }
    // 6. Set selected timezone ID (handled in loadTimezone method)
    // Call setSelectedTimezoneId if timezones are already loaded
    if (user.timezoneId && this.timeZones.length > 0) {
      this.setSelectedTimezoneId(user.timezoneId);
    }
  }

  private populateRoleSpecificFields(hierarchicalData: UserHierarchicalData, roleId: string): void {
    const role = this.roles.find(r => r.id === roleId)?.name;

    if (this.roleMatches(role, UserRole.INSTALLER)) {
      const clientId = hierarchicalData.customers ? hierarchicalData.customers[0]?.clientId : null;
      const clientName = hierarchicalData.customers ? hierarchicalData.customers[0]?.clientName : '';
      // Pre-populate client in dropdown so it's visible immediately
      if (clientId && !this.clients.find(c => c.id === clientId)) {
        this.clients = [{ id: clientId, name: clientName || clientId }, ...this.clients];
      }
      this.userForm.patchValue({ clientId: clientId });
      this.onClientChange(clientId || '');

      const customerIds = hierarchicalData.customers ? hierarchicalData.customers.map(c => c.id) : [];
      this.customerIds.setValue(customerIds, { emitEvent: false });
      this.userForm.patchValue({ customerIds: customerIds }, { emitEvent: false });

      this.loadLocationsByCustomers(customerIds);
    } else if (
      this.roleMatches(role, UserRole.USER) ||
      this.roleMatches(role, UserRole.RETAIL_ADMIN) ||
      this.roleMatches(role, UserRole.RETAIL_MANAGER) ||
      this.roleMatches(role, UserRole.RETAIL_USER) ||
      this.roleMatches(role, UserRole.RETAIL_USER_WITH_CONTROL)
    ) {
      const clientId = hierarchicalData.locations ? hierarchicalData.locations[0]?.clientId : null;
      const clientName = hierarchicalData.locations ? hierarchicalData.locations[0]?.clientName : '';
      // Pre-populate client in dropdown so it's visible immediately
      if (clientId && !this.clients.find(c => c.id === clientId)) {
        this.clients = [{ id: clientId, name: clientName || clientId }, ...this.clients];
      }
      this.userForm.patchValue({ clientId: clientId });
      this.onClientChange(clientId || '');

      const customerIds = hierarchicalData.locations ? [...new Set(hierarchicalData.locations.map(c => c.customerId))] : [];
      const locationIds = hierarchicalData.locations ? hierarchicalData.locations.map(l => l.id) : [];

      this.customerIds.setValue(customerIds, { emitEvent: false });
      this.locationIds.setValue(locationIds, { emitEvent: false });
      this.userForm.patchValue({
        customerIds: customerIds,
        locationIds: locationIds
      }, { emitEvent: false });

      this.loadLocationsByCustomers(customerIds);
    } else if (this.roleMatches(role, UserRole.CUSTOMER_ADMIN)) {
      const clientId = hierarchicalData.departments ? hierarchicalData.departments[0]?.clientId : null;
      const clientName = hierarchicalData.departments ? hierarchicalData.departments[0]?.clientName : '';
      // Pre-populate client in dropdown so it's visible immediately
      if (clientId && !this.clients.find(c => c.id === clientId)) {
        this.clients = [{ id: clientId, name: clientName || clientId }, ...this.clients];
      }
      this.userForm.patchValue({ clientId: clientId });
      this.onClientChange(clientId || '');

      const customerIds = hierarchicalData.departments ? [...new Set(hierarchicalData.departments.map(c => c.customerId))] : [];
      const locationIds = hierarchicalData.departments ? [...new Set(hierarchicalData.departments.map(l => l.locationId))] : [];
      const buildingIds = hierarchicalData.departments ? [...new Set(hierarchicalData.departments.map(b => b.buildingId))] : [];
      const floorIds = hierarchicalData.departments ? [...new Set(hierarchicalData.departments.map(f => f.floorId))] : [];
      const departmentIds = hierarchicalData.departments ? hierarchicalData.departments.map(d => d.id) : [];

      this.customerIds.setValue(customerIds, { emitEvent: false });
      this.locationIds.setValue(locationIds, { emitEvent: false });
      this.buildingIds.setValue(buildingIds, { emitEvent: false });
      this.floorIds.setValue(floorIds, { emitEvent: false });
      this.departmentIds.setValue(departmentIds, { emitEvent: false });

      this.userForm.patchValue({
        customerIds: customerIds,
        locationIds: locationIds,
        buildingIds: buildingIds,
        floorIds: floorIds,
        departmentIds: departmentIds
      }, { emitEvent: false });

      this.loadLocationsByCustomers(customerIds);
    } else if (this.roleMatches(role, UserRole.CLIENT_ADMIN)) {
      const clientId = hierarchicalData.clients ? hierarchicalData.clients[0]?.id : null;
      const clientName = hierarchicalData.clients ? hierarchicalData.clients[0]?.name : '';
      // Pre-populate client in dropdown so it's visible immediately
      if (clientId && !this.clients.find(c => c.id === clientId)) {
        this.clients = [{ id: clientId, name: clientName || clientId }, ...this.clients];
      }
      this.userForm.patchValue({ clientId: clientId });
      this.onClientChange(clientId || '');
    } else if (this.roleMatches(role, UserRole.SUPER_ADMIN)) {
      // No hierarchical data needed for Super Admin
    }
    else {
      // Default case for unknown roles
    }
  }

  private extractHierarchyFromDepartments(departments: DepartmentData[]): void {
    // Extract unique entities at each level
    const uniqueCustomers = new Map();
    const uniqueLocations = new Map();
    const uniqueBuildings = new Map();
    const uniqueFloors = new Map();

    departments.forEach(dept => {
      uniqueCustomers.set(dept.customerId, {
        id: dept.customerId,
        name: dept.customerName
      });

      uniqueLocations.set(dept.locationId, {
        id: dept.locationId,
        name: dept.locationName
      });

      uniqueBuildings.set(dept.buildingId, {
        id: dept.buildingId,
        name: dept.buildingName
      });

      uniqueFloors.set(dept.floorId, {
        id: dept.floorId,
        name: dept.floorName
      });
    });

    // Populate all dropdowns
    this.customers = Array.from(uniqueCustomers.values());
    this.locations = Array.from(uniqueLocations.values());
    this.buildings = Array.from(uniqueBuildings.values());
    this.floors = Array.from(uniqueFloors.values());

    // Set selected values
    this.customerIds.setValue(Array.from(uniqueCustomers.keys()));
    this.locationIds.setValue(Array.from(uniqueLocations.keys()));
    this.buildingIds.setValue(Array.from(uniqueBuildings.keys()));
    this.floorIds.setValue(Array.from(uniqueFloors.keys()));
  }

  /**
   * Update role-based field visibility when role changes
   */
  private updateRoleBasedFields(roleId: string): void {
    // Find the role name from roleId
    if (!this.roles || this.roles.length === 0) {
      console.warn('Roles not loaded yet, cannot update role-based fields');
      return;
    }

    const selectedRole = this.roles.find(role => role.id === roleId);
    if (selectedRole) {
      // Update current role fields for visibility
      this.currentRoleFields = this.roleFieldsConfig[selectedRole.name] || [];

      // Clear form controls for fields that should not be visible
      this.clearInvisibleFields(selectedRole.name);
    }
  }

  /**
   * Clear form controls for fields that are not visible for the current role
   */
  private clearInvisibleFields(roleName: string): void {
    const requiredFields = this.roleFieldsConfig[roleName] || [];

    // Clear fields that are not required for this role
    if (!requiredFields.includes('ClientId')) {
      this.userForm.patchValue({ clientId: '' });
      this.clients = [];
    }

    if (!requiredFields.includes('CustomerIds')) {
      this.customerIds.setValue([]);
      this.customers = [];
    }

    if (!requiredFields.includes('LocationIds')) {
      this.locationIds.setValue([]);
      this.locations = [];
    }

    if (!requiredFields.includes('BuildingIds')) {
      this.buildingIds.setValue([]);
      this.buildings = [];
    }

    if (!requiredFields.includes('FloorIds')) {
      this.floorIds.setValue([]);
      this.floors = [];
    }

    if (!requiredFields.includes('DepartmentIds')) {
      this.departmentIds.setValue([]);
      this.departments = [];
    }
  }


  onSubmit(): void {
    if (this.userForm.valid) {
      const formData = this.userForm.getRawValue();
      const userRequest: UserRequest = {
        // Account Details
        email: formData.email,
        displayName: formData.displayName ?? '',

        // Personal Details
        firstName: formData.firstName,
        middleName: formData.middleName ?? '',
        lastName: formData.lastName,
        phone: combinePhone(formData.phoneCountryCode, formData.phone),
        temperatureUnit: formData.temperatureUnit,

        // Address Details
        timezoneId: formData.timezoneId ?? '',
        address1: formData.address1,
        address2: formData.address2 ?? '',
        city: formData.city ?? '',
        countryGuid: this.commonService.parseNullValue(formData.countryGuid),
        stateGuid: this.commonService.parseNullValue(formData.stateGuid),
        zipcode: formData.zipcode,

        // Role-based Dynamic Fields
        roleId: formData.systemRole,
        clientId: this.commonService.parseNullValue(formData.clientId),
        // departmentIds contains different entity IDs based on selected role
        departmentIds: this.getDepartmentIdsBasedOnRole(formData.systemRole),
        isActive: formData.isActive,
        // IOT Timezone ID and IOT Role ID
        iotTimezoneId: this.selectedIOTTimezoneId || this.getTimezoneIdFallback(formData.timezoneId),
        iotRoleId: this.selectedIOTRoleId || this.getRoleIdFallback(formData.systemRole)
      };

      if (this.isEditMode) {
        this.updateUser(formData.userId, userRequest);
      } else {
        this.createUser(userRequest);
      }
    } else {
      this.markFormGroupTouched(this.userForm);
      this.customToastrService.error('Please fill in all required fields correctly');
    }
  }

  createUser(userRequest: UserRequest): void {
    this.userService.createUser(userRequest).subscribe({
      next: (response) => {
        // After user is created successfully, call the userrole API
        const userRoleRequest = {
          userId: response,
          roleId: userRequest.roleId
        };

        this.userService.addUserRole(userRoleRequest).subscribe({
          next: () => {
            this.customToastrService.success('User created successfully');
            this.userSaved.emit(response);
            this.closeDialog();
          },
          error: (userRoleError) => {
            console.error('Error creating user role:', userRoleError);
            this.customToastrService.error('User created but failed to assign role');
            this.userSaved.emit(response);
            this.closeDialog();
          }
        });
      },
      error: (error) => {
        console.error('Error creating user:', error);
        this.customToastrService.error('Failed to create user');
      }
    });
  }

  updateUser(userId: string, userRequest: UserRequest): void {
    this.userService.updateUser(userId, userRequest).subscribe({
      next: (response) => {
        const userRoleRequest = {
          userId: userId,
          roleId: userRequest.roleId
        };
        this.userService.addUserRole(userRoleRequest).subscribe({
          next: () => {
            this.customToastrService.success('User updated successfully');
            this.userSaved.emit(response);
            this.closeDialog();
          },
          error: (userRoleError) => {
            console.error('Error updating user role:', userRoleError);
            this.customToastrService.error('User updated but failed to assign role');
            this.userSaved.emit(response);
            this.closeDialog();
          }
        });
      },
      error: (error) => {
        console.error('Error updating user:', error);
        this.customToastrService.error('Failed to update user');
      }
    });
  }

  closeDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close(true);
    } else {
      this.router.navigate(['/usermanagement']);
    }
  }

  cancelDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close(false);
    } else {
      this.router.navigate(['/usermanagement']);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // Form validation helpers
  isFieldInvalid(fieldName: string): boolean {
    const field = this.userForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.userForm.get(fieldName);
    if (field && field.errors) {
      if (field.errors['required']) {
        return `${this.getFieldLabel(fieldName)} is required`;
      }
      if (field.errors['email']) {
        return 'Please enter a valid email address';
      }
      if (field.errors['minlength']) {
        return `${this.getFieldLabel(fieldName)} must be at least ${field.errors['minlength'].requiredLength} characters`;
      }
      if (field.errors['maxlength']) {
        return `${this.getFieldLabel(fieldName)} cannot exceed ${field.errors['maxlength'].requiredLength} characters`;
      }
      if (field.errors['pattern']) {
        if (fieldName === 'phone') {
          return 'Please enter a valid phone number';
        }
        return 'Please enter a valid format';
      }
    }
    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      emailId: 'Email',
      systemRole: 'System Role',
      displayName: 'Display Name',
      firstName: 'First Name',
      middleName: 'Middle Name',
      lastName: 'Last Name',
      phone: 'Phone',
      temperatureUnit: 'Temperature Unit',
      timezoneId: 'Timezone',
      address1: 'Address 1',
      address2: 'Address 2',
      city: 'City',
      countryGuid: 'Country',
      stateGuid: 'State',
      zipcode: 'Zip Code',
      clientId: 'Client',
      customerIds: 'Customers',
      locationIds: 'Locations',
      buildingIds: 'Buildings',
      floorIds: 'Floors',
      departmentIds: 'Departments'
    };
    return labels[fieldName] || fieldName;
  }
}